import React, { type Dispatch, type SetStateAction, useEffect, useState } from 'react';

import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';

import { CloseIcon } from '../../assets/icons/close.tsx';
import { SearchIcon } from '../../assets/icons/search.tsx';
import { Button } from '../button/index.ts';
import { Input, type InputPT } from '../input/index.ts';

type PT = {
  onOpen?: () => void;
  onClose?: () => void;
  width?: string | number;
  minWidth?: string | number;
  isOpenInit?: boolean;
  isClosedControlled?: boolean;
  setIsClosedControlled?: Dispatch<SetStateAction<boolean>>;
  isCanClose?: boolean;
  isButtonShouldBeFullWidth?: boolean;
} & InputPT;

export const SearchInput = (props: PT) => {
  const {
    onOpen,
    onClose,
    value: initValue,
    onChange,
    isOpenInit = false,
    isClosedControlled = false,
    setIsClosedControlled,
    isCanClose = true,
    width = 288,
    minWidth,
    isButtonShouldBeFullWidth = false,
    sx,
    ...rest
  } = props;

  const { spacing } = useTheme();
  const [isSearchOpen, setIsSearchOpen] = useState(Boolean(isOpenInit));
  const [value, setValue] = useState(initValue ?? '');

  useEffect(() => {
    setValue(initValue ?? '');
  }, [initValue]);

  useEffect(() => {
    if (isClosedControlled) {
      setIsSearchOpen(false);
    }
  }, [isClosedControlled]);

  const handleOpen = () => {
    setIsClosedControlled?.(false);
    setIsSearchOpen(true);
    onOpen?.();
  };

  const handleClose = () => {
    setIsSearchOpen(false);
    onClose?.();
    setValue('');
    onChange?.({ target: { value: '' } } as React.ChangeEvent<HTMLInputElement>);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        height: '48px',
        width: isSearchOpen ? width : undefined,
        minWidth: isSearchOpen ? (minWidth ?? width) : undefined,
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
      }}
    >
      {!isSearchOpen && (
        <Button
          variantStyle="filter"
          withoutColon
          fullWidth={isButtonShouldBeFullWidth}
          sx={
            isButtonShouldBeFullWidth
              ? {
                  justifyContent: 'space-between',
                }
              : undefined
          }
          disabled={rest.disabled}
          onClick={handleOpen}
        >
          Search
          <SearchIcon />
        </Button>
      )}

      {isSearchOpen && (
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            width,
            minWidth: minWidth ?? width,
            zIndex: 2,
          }}
        >
          <Input
            {...rest}
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              onChange?.(e);
            }}
            focused
            autoFocus
            sx={{
              width: '100%',
              '& .MuiFilledInput-root': {
                px: 4,
                py: 3,
                pr: 8,
                borderRadius: spacing(3),
                height: '48px',
              },
              ...sx,
            }}
          />

          {isCanClose && <StyledCloseIcon onClick={handleClose} />}
        </Box>
      )}
    </Box>
  );
};

const StyledCloseIcon = styled(CloseIcon)(({ theme }) => ({
  position: 'absolute',
  right: '12px',
  top: '50%',
  transform: 'translateY(-50%)',
  color: theme.palette.dark,
  '&:hover': {
    cursor: 'pointer',
  },
}));
